/**
 * InitPage — 上线初始化（Jenkins 式）
 * 未初始化时全站唯一入口：key 页 → 单浏览器认领 → 单步向导（创建管理员）。
 * key 页提示 key 保存位置；认领后仅该浏览器可进入向导，其他浏览器停留 key 页。
 */
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  makeStyles,
  tokens,
  Text,
  Input,
  Button,
  Card,
  CardHeader,
  Field,
  InfoLabel,
  Spinner,
  MessageBar,
  MessageBarBody,
} from '@fluentui/react-components'
import { KeyRegular, LockClosedRegular } from '@fluentui/react-icons'
import { initApi } from '@/api/init'
import { ROUTES } from '@/routes'
import { Navigate } from 'react-router-dom'

const useStyles = makeStyles({
  root: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: `linear-gradient(160deg, ${tokens.colorBrandBackground2} 0%, ${tokens.colorNeutralBackground1} 60%)`,
    padding: '24px',
  },
  card: {
    width: '460px',
    maxWidth: '92vw',
    padding: '24px',
  },
  title: {
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightSemibold,
  },
  subtitle: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    marginTop: '4px',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    marginTop: '20px',
  },
  hint: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    lineHeight: '18px',
  },
  action: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '8px',
    marginTop: '4px',
  },
})

function CenteredSplash() {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Spinner size="large" label="加载中..." />
    </div>
  )
}

/** Key 入口页 */
function KeyEntry() {
  const styles = useStyles()
  const queryClient = useQueryClient()
  const [key, setKey] = useState('')
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const onSubmit = async () => {
    if (!key.trim()) {
      setError('请输入初始化 Key')
      return
    }
    setSubmitting(true)
    setError('')
    try {
      await initApi.verify(key)
      await queryClient.invalidateQueries({ queryKey: ['init-status'] })
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className={styles.root}>
      <Card className={styles.card}>
        <CardHeader
          header={<div className={styles.title}>初始化 Mailman</div>}
        description={<div className={styles.subtitle}>系统尚未初始化，请输入初始化 Key 以开始配置</div>}
      />
      <div className={styles.form}>
        <Field label={<InfoLabel htmlFor="init-key">初始化 Key</InfoLabel>} required>
          <Input
            id="init-key"
            type="password"
            value={key}
            onChange={(_, d) => setKey(d.value)}
            placeholder="粘贴启动时生成的 Key"
            onKeyDown={(e) => e.key === 'Enter' && onSubmit()}
          />
        </Field>

        {error && (
          <MessageBar intent="error">
            <MessageBarBody>{error}</MessageBarBody>
          </MessageBar>
        )}

        <Text block className={styles.hint}>
          初始化 Key 已保存于服务器 <code>data_dir/init_key</code> 文件，并打印在启动日志中。
          输入正确 Key 后，仅当前浏览器可进入初始化向导。
        </Text>

        <div className={styles.action}>
          <Button appearance="primary" icon={<KeyRegular />} onClick={onSubmit} disabled={submitting}>
            {submitting ? '校验中...' : '进入初始化'}
          </Button>
        </div>
      </div>
      </Card>
    </div>
  )
}

/** 单步向导：创建管理员账号 */
function Wizard() {
  const styles = useStyles()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const onSubmit = async () => {
    const addr = email.trim().toLowerCase()
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(addr)) {
      setError('请输入有效的管理员邮箱')
      return
    }
    if (password.length < 8) {
      setError('密码至少 8 位')
      return
    }
    if (password !== confirm) {
      setError('两次输入的密码不一致')
      return
    }
    setSubmitting(true)
    setError('')
    try {
      await initApi.complete(addr, password)
      await queryClient.invalidateQueries({ queryKey: ['init-status'] })
      navigate(ROUTES.LOGIN, { replace: true })
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className={styles.root}>
      <Card className={styles.card}>
        <CardHeader
          header={<div className={styles.title}>创建管理员账号</div>}
        description={<div className={styles.subtitle}>完成初始化所需的最后一步</div>}
      />
      <div className={styles.form}>
        <Field label={<InfoLabel htmlFor="init-email">管理员邮箱</InfoLabel>} required hint="该邮箱所在域将作为主域">
          <Input
            id="init-email"
            value={email}
            onChange={(_, d) => setEmail(d.value)}
            placeholder="admin@example.com"
          />
        </Field>
        <Field label={<InfoLabel htmlFor="init-pass">密码</InfoLabel>} required hint="至少 8 位">
          <Input
            id="init-pass"
            type="password"
            value={password}
            onChange={(_, d) => setPassword(d.value)}
            placeholder="设置管理员登录密码"
          />
        </Field>
        <Field label={<InfoLabel htmlFor="init-confirm">确认密码</InfoLabel>} required>
          <Input
            id="init-confirm"
            type="password"
            value={confirm}
            onChange={(_, d) => setConfirm(d.value)}
            placeholder="再次输入密码"
            onKeyDown={(e) => e.key === 'Enter' && onSubmit()}
          />
        </Field>

        {error && (
          <MessageBar intent="error">
            <MessageBarBody>{error}</MessageBarBody>
          </MessageBar>
        )}

        <div className={styles.action}>
          <Button appearance="primary" icon={<LockClosedRegular />} onClick={onSubmit} disabled={submitting}>
            {submitting ? '提交中...' : '完成初始化'}
          </Button>
        </div>
      </div>
      </Card>
    </div>
  )
}

export default function InitPage() {
  const { data, isLoading } = useQuery({
    queryKey: ['init-status'],
    queryFn: initApi.status,
    // 未初始化时轮询：认领 / 他方完成初始化后自动切换
    refetchInterval: 5000,
  })

  if (isLoading || !data) return <CenteredSplash />

  if (data.initialized) return <Navigate to={ROUTES.LOGIN} replace />
  return data.claimedByMe ? <Wizard /> : <KeyEntry />
}